import { useMemo } from "react";
import { Shield, Check, ArrowRight, MapPin } from "lucide-react";
import type {
  CustomStoreProduct,
  DtpCategoryContentResponse,
} from "@bloom/shared";
import { CategoryIcon } from "./CategoryIcon";
import { HeroIllustration } from "./HeroIllustration";
import { getFamilyDisplayName } from "./useEducationalCatalog";
import type { ClinicInfo } from "./EducationalShell";
import "./educational-home.css";

const CATEGORY_TINTS: Record<string, string> = {
  "weight-loss": "bg-[#eaf2ec] text-[var(--store-accent,#2d4a3a)]",
  "oral-ed": "bg-[#ede3d8] text-[#6b4a26]",
  "sexual-health": "bg-[#fbe8e8] text-[#8b3c3c]",
  dermatology: "bg-[#f5ebd8] text-[#8b6332]",
  peptides: "bg-[#efe8f5] text-[#5a4a6a]",
  "daily-vitamins": "bg-[#e3eef5] text-[#3a5a78]",
};

export function EducationalHero({
  clinic,
  products,
  onProduct,
  heading,
  introduction,
  heroImage,
}: {
  clinic: ClinicInfo;
  products: CustomStoreProduct[];
  onProduct?: (id: string) => void;
  heading?: string;
  introduction?: string;
  heroImage?: string;
}) {
  const coverage = clinic.coverage;
  return (
    <section>
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 pt-12 pb-16 md:pt-20 md:pb-24 educational-hero-layout grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-center">
        <div className="md:col-span-7">
          <div className="text-[11px] md:text-xs uppercase tracking-[0.2em] text-[var(--store-muted,#8a8175)] mb-4 md:mb-6">
            {clinic.name}'s curated formulary
          </div>
          <h1 className="font-serif text-[2.25rem] sm:text-[2.75rem] md:text-[3.5rem] lg:text-[4.5rem] font-medium leading-[0.95] tracking-tight">
            {heading ?? clinic.theme?.headline ?? (
              <>
                Medicine,
                <br />
                <em className="font-normal">prescribed</em> for you.
              </>
            )}
          </h1>
          <p className="mt-6 md:mt-8 text-base md:text-lg text-[var(--store-muted,#4a4238)] max-w-xl leading-relaxed">
            {introduction ?? clinic.theme?.description ?? (
              <>
                Each medication in this store has been selected by {clinic.name}{" "}
                for our patients. Read about what each one does, how it works,
                and whether it's right for you — then request a consult.
              </>
            )}
          </p>
          <div className="mt-8 md:mt-10 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-sm text-[var(--store-muted,#8a8175)]">
            {coverage && (
              <span className="flex items-center gap-2">
                {coverage.kind === "single"
                  ? <Shield className="w-4 h-4 text-[var(--store-accent,#2d4a3a)]" />
                  : <MapPin className="w-4 h-4 text-[var(--store-accent,#2d4a3a)]" />}
                {coverage.label}
              </span>
            )}
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[var(--store-accent,#2d4a3a)]" />
              503A pharmacy
            </span>
          </div>
        </div>
        <div className="md:col-span-5">
          {heroImage ? (
            <img
              src={heroImage}
              alt=""
              className="w-full aspect-[4/5] object-cover rounded-3xl"
            />
          ) : (
            <FeaturedHero
              clinicFeatured={clinic.featuredProductId}
              products={products}
              onClick={(p) => {
                onProduct?.(p._id);
              }}
            />
          )}
        </div>
      </div>
    </section>
  );
}

export function EducationalCategories({
  products,
  categoryContent,
  onCategory,
}: {
  products: CustomStoreProduct[];
  categoryContent: DtpCategoryContentResponse[];
  onCategory?: (slug: string) => void;
}) {
  const topLevelCategories = useMemo(() => {
    const byParent: Record<string, DtpCategoryContentResponse[]> = {};
    const topLevel: DtpCategoryContentResponse[] = [];
    for (const c of categoryContent) {
      if (c.parentSlug) {
        if (!byParent[c.parentSlug]) byParent[c.parentSlug] = [];
        byParent[c.parentSlug].push(c);
      } else {
        topLevel.push(c);
      }
    }
    return topLevel
      .map((c) => {
        const childSlugs = new Set([
          c.slug,
          ...(byParent[c.slug] || []).map((s) => s.slug),
        ]);
        const familySet = new Set<string>();
        let optionCount = 0;
        for (const p of products) {
          if (p.primaryCategorySlug && childSlugs.has(p.primaryCategorySlug)) {
            if (p.drugFamilySlug) familySet.add(p.drugFamilySlug);
            optionCount++;
          }
        }
        return { cat: c, familyCount: familySet.size, optionCount };
      })
      .filter((c) => c.optionCount > 0)
      .sort((a, b) => (a.cat.order ?? 0) - (b.cat.order ?? 0));
  }, [categoryContent, products]);

  const totalFamilies = topLevelCategories.reduce(
    (sum, c) => sum + c.familyCount,
    0,
  );

  return (
    <section className="bg-white border-t border-[var(--store-border,#e8e2d6)]">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-14 md:py-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-12">
          <div>
            <div className="text-[11px] md:text-xs uppercase tracking-[0.2em] text-[var(--store-muted,#8a8175)] mb-3">
              Browse
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-medium tracking-tight">
              What brings you here?
            </h2>
          </div>
          {totalFamilies > 0 && (
            <div className="text-sm text-[var(--store-muted,#8a8175)]">
              {topLevelCategories.length} treatment{" "}
              {topLevelCategories.length === 1 ? "area" : "areas"} ·{" "}
              {totalFamilies} medications
            </div>
          )}
        </div>

        {topLevelCategories.length === 0 ? (
          <div className="text-center py-16 text-[var(--store-muted,#8a8175)]">
            No products available in this store yet.
          </div>
        ) : (
          <div className="educational-category-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {topLevelCategories.map(({ cat, familyCount }) => (
              <button
                key={cat.slug}
                disabled={!onCategory}
                onClick={() => onCategory?.(cat.slug)}
                className="text-left transition-transform hover:-translate-y-0.5"
              >
                <CategoryArt slug={cat.slug} iconKey={cat.iconKey} />
                <div className="pt-4 md:pt-5">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-serif text-[20px] md:text-[22px] font-medium">
                      {cat.displayName}
                    </h3>
                    <span className="text-xs text-[var(--store-muted,#8a8175)] flex-shrink-0">
                      {familyCount} {familyCount === 1 ? "option" : "options"}
                    </span>
                  </div>
                  {cat.shortPitch && (
                    <p className="text-[14px] md:text-[15px] text-[var(--store-muted,#4a4238)] mt-2 leading-relaxed">
                      {cat.shortPitch}
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function FeaturedHero({
  clinicFeatured,
  products,
  onClick,
}: {
  clinicFeatured?: string;
  products: CustomStoreProduct[];
  onClick: (product: CustomStoreProduct) => void;
}) {
  const featured = clinicFeatured
    ? products.find((p) => p._id === clinicFeatured)
    : null;

  if (!featured) {
    return (
      <div className="relative aspect-[4/5] rounded-3xl bg-gradient-to-br from-[#e8f0e9] via-[#d4c9b5] to-[#b89876] shadow-[0_30px_60px_-20px_rgba(26,22,18,0.25)] overflow-hidden">
        <HeroIllustration slug="landing" />
      </div>
    );
  }

  const familyName = featured.drugFamilySlug
    ? getFamilyDisplayName(featured.drugFamilySlug, products)
    : featured.drugName || featured.displayName;

  return (
    <button
      onClick={() => onClick(featured)}
      className="block w-full text-left group"
    >
      <div className="aspect-[4/5] rounded-3xl bg-gradient-to-br from-[#e8f0e9] via-[#d4c9b5] to-[#b89876] shadow-[0_30px_60px_-20px_rgba(26,22,18,0.25)] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(255,255,255,0.6),transparent_60%)]" />
        <HeroIllustration slug={featured.primaryCategorySlug} />
        <div className="absolute bottom-6 left-6 right-6 md:bottom-8 md:left-8 md:right-8 text-white">
          <div className="text-[10px] md:text-xs uppercase tracking-widest opacity-80 mb-2">
            Featured this month
          </div>
          <div className="font-serif text-xl md:text-2xl font-medium leading-tight">
            {familyName}
          </div>
          <div className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium opacity-90 group-hover:opacity-100">
            Learn more{" "}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </div>
        </div>
      </div>
    </button>
  );
}

function CategoryArt({ slug, iconKey }: { slug: string; iconKey?: string }) {
  const tint = CATEGORY_TINTS[slug] || "bg-[#ede3d8] text-[#6b4a26]";
  return (
    <div
      className={`relative aspect-[4/3] rounded-2xl flex items-center justify-center overflow-hidden ${tint}`}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.5),transparent_55%)]" />
      <div className="absolute w-[60%] aspect-square rounded-full bg-current opacity-[0.08]" />
      <div className="relative w-[44%] max-w-[96px] aspect-square">
        <CategoryIcon iconKey={iconKey} />
      </div>
    </div>
  );
}
